"use client"

import { useEffect } from "react"

const CHAT_URL =
  process.env.NEXT_PUBLIC_MINT_AI_CHAT_URL ||
  "https://ai-web-dev-a8eqcwajeufmh0ee.westeurope-01.azurewebsites.net/chat"

const SDK_URL = CHAT_URL.replace("/chat", "/sdk/chat-widget.js")

export const MINT_AI_CHAT_ORIGIN = new URL(CHAT_URL).origin

const ALLOWED_REDIRECT_HOSTS = [
  "ashtrom.co.il",
  "ashtrom4rent.co.il",
  "ashtromresidencesforrent.co.il",
  "ai.mintapp.co.il",
]

function isAllowedRedirectUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return ALLOWED_REDIRECT_HOSTS.some(
      (d) => parsed.hostname === d || parsed.hostname.endsWith("." + d),
    )
  } catch {
    return false
  }
}

const chatConfiguration = {
  chatId: "web",
  desktopIframeStyle: {
    position: "fixed",
    bottom: "-900px",
    right: "unset",
    left: "40px",
    width: "375px",
    height: "700px",
    border: "1px solid #ccc",
    borderRadius: "24px",
    zIndex: "99999999",
    display: "block",
    overflow: "auto",
    scrolling: "yes",
    transition: "bottom 0.5s ease-in-out",
    boxShadow: "0 12px 24px 0 rgba(0, 0, 0, 0.24)",
  },
  mobileIframeStyle: {
    position: "fixed",
    bottom: "-100%",
    left: "0",
    width: "100%",
    height: "100dvh",
    border: "none",
    borderRadius: "0",
    zIndex: "99999999",
    display: "none",
    overflow: "hidden",
    scrolling: "yes",
    transition: "bottom 0.5s ease-in-out",
    maxHeight: "100dvh",
    WebkitTransform: "translate3d(0,0,0)",
    transform: "translate3d(0,0,0)",
  },
  defaultChatData: {},
  defaultChatStyles: {},
  onChatOpened: () => {},
  onChatClosed: () => {},
  onRedirect: (url: string) => {
    if (url.startsWith("/")) {
      if (window.next && window.next.router) {
        window.next.router.push(url)
      } else {
        window.location.href = url
      }
    } else if (isAllowedRedirectUrl(url)) {
      window.location.href = url
    }
  },
}

export function loadSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.MintAIChatWidget) {
      resolve()
      return
    }
    const existing = document.querySelector(`script[src="${SDK_URL}"]`)
    if (existing) {
      existing.addEventListener("load", () => resolve())
      return
    }
    const s = document.createElement("script")
    s.src = SDK_URL
    s.onload = () => resolve()
    s.onerror = () => reject(new Error("[MintAI] Failed to load SDK"))
    document.head.appendChild(s)
  })
}

export function initWidget() {
  if (!window.MintAIChatWidget) return

  const staleIframe = document.querySelector("iframe[src*='chat']")
  if (staleIframe) staleIframe.remove()

  if (window.mintAiWidget) {
    try { window.mintAiWidget.remove() } catch { /* already gone */ }
  }

  window.mintAiWidget = window.MintAIChatWidget.create(chatConfiguration, CHAT_URL)
}

function setupMobileKeyboardHandler() {
  if (!window.visualViewport) return

  let cachedIframe: HTMLIFrameElement | null = null
  const getIframe = () => {
    if (!cachedIframe || !cachedIframe.isConnected) {
      cachedIframe = document.querySelector("iframe[src*='chat']") as HTMLIFrameElement | null
    }
    return cachedIframe
  }

  const onResize = () => {
    const f = getIframe()
    if (f && window.innerWidth <= 768) {
      f.style.height = window.visualViewport!.height + "px"
      f.style.maxHeight = window.visualViewport!.height + "px"
      f.style.top = "0"
      f.style.bottom = "auto"
    }
  }
  const onScroll = () => {
    const f = getIframe()
    if (f && window.innerWidth <= 768) {
      f.style.top = window.visualViewport!.offsetTop + "px"
    }
  }

  window.visualViewport.addEventListener("resize", onResize)
  window.visualViewport.addEventListener("scroll", onScroll)

  return () => {
    window.visualViewport?.removeEventListener("resize", onResize)
    window.visualViewport?.removeEventListener("scroll", onScroll)
  }
}

export default function MintAiWidgetInit() {
  useEffect(() => {
    let cleanupKeyboard: (() => void) | undefined

    // Defer SDK loading to avoid competing with page hydration
    const timer = "requestIdleCallback" in window
      ? (window as /* eslint-disable-line */ any).requestIdleCallback(() => {
          loadSdk()
            .then(() => {
              initWidget()
              cleanupKeyboard = setupMobileKeyboardHandler()
            })
            .catch(console.error)
        })
      : setTimeout(() => {
          loadSdk()
            .then(() => {
              initWidget()
              cleanupKeyboard = setupMobileKeyboardHandler()
            })
            .catch(console.error)
        }, 3000)

    return () => {
      if ("cancelIdleCallback" in window) {
        (window as /* eslint-disable-line */ any).cancelIdleCallback(timer)
      } else {
        clearTimeout(timer)
      }
      cleanupKeyboard?.()
    }
  }, [])

  return null
}
